import { useState, useRef, useMemo } from "react";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { analytics } from "./firebase";
import { styled } from "@mui/material/styles";
import {
    Box,
    Button,
    CssBaseline,
    Grid,
    Toolbar,
    AppBar,
    Typography,
    Snackbar,
    useMediaQuery,
    IconButton,
} from "@mui/material";
import {
    CloudDownload,
    Google,
    ContentCopy,
    DarkMode,
    LightMode,
} from "@mui/icons-material";
import Editor from "./components/Editor";
import Preview from "./components/Preview";
import Auth from "./components/Auth";
import SEO from "./seo";
import html2pdf from "html2pdf.js";
import {
    Document,
    Packer,
    Paragraph,
    TextRun,
    HeadingLevel,
    Table,
    TableRow,
    TableCell,
    BorderStyle,
    WidthType,
} from "docx";
import { saveAs } from "file-saver";
import { logEvent } from "firebase/analytics";

const Root = styled("div")(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor:
        theme.palette.mode === "dark"
            ? "#0f172a"
            : theme.palette.background.default,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        paddingTop: theme.spacing(1),
        paddingBottom: theme.spacing(1),
        gap: theme.spacing(1),
    },
}));

const LeftSection = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
}));

const RightSection = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {
        flexWrap: "wrap",
        justifyContent: "center",
    },
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    padding: theme.spacing(1, 2),
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    "&:hover": {
        boxShadow: "0 4px 6px rgba(0,0,0,0.15)",
    },
}));

const ContentContainer = styled(Grid)(({ theme }) => ({
    height: "calc(100vh - 64px)",
    [theme.breakpoints.down("md")]: {
        height: "auto",
    },
}));

const EditorContainer = styled(Grid)(({ theme }) => ({
    height: "100%",
    [theme.breakpoints.down("md")]: {
        height: "50vh",
    },
}));

const PreviewContainer = styled(Grid)(({ theme }) => ({
    height: "100%",
    [theme.breakpoints.down("md")]: {
        height: "50vh",
    },
}));

export default function App() {
    // Get theme mode from localStorage or default to 'light'
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem("themeMode");
        return savedMode ? savedMode : "light";
    });
    const theme = useMemo(
        () =>
            createTheme({
                palette: {
                    mode,
                },
            }),
        [mode]
    );
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const [markdown, setMarkdown] = useState(
        "# Hello World\nStart typing here..."
    );
    const [authOpen, setAuthOpen] = useState(false);
    const [loading, setLoading] = useState({
        pdf: false,
        docx: false,
        google: false,
    });
    const [copySuccess, setCopySuccess] = useState(false);
    const [googleToken, setGoogleToken] = useState(null);
    const previewRef = useRef(null);

    // Updated dark mode toggle with localStorage persistence
    const toggleDarkMode = () => {
        const newMode = mode === "dark" ? "light" : "dark";
        setMode(newMode);
        localStorage.setItem("themeMode", newMode);
        logEvent(analytics, "toggled_theme_mode", { mode: newMode });
    };

    // Updated copy to clipboard function to copy rich text
    const handleCopy = async () => {
        try {
            if (!previewRef.current) {
                throw new Error("Preview content not found");
            }

            logEvent(analytics, "clicked_copy_to_clipboard", {});

            const htmlContent = previewRef.current.innerHTML;

            // Create a new clipboard item with both HTML and plain text formats
            const clipboardItem = new ClipboardItem({
                "text/html": new Blob([htmlContent], { type: "text/html" }),
                "text/plain": new Blob([previewRef.current.innerText], {
                    type: "text/plain",
                }),
            });

            await navigator.clipboard.write([clipboardItem]);
            setCopySuccess(true);
            logEvent(analytics, "copied_rich_text", {});
        } catch (err) {
            // Fallback for browsers that don't support ClipboardItem
            try {
                const tempElem = document.createElement("div");
                tempElem.innerHTML = previewRef.current.innerHTML;

                // Clean up any unwanted elements or attributes
                const cleanHtml = tempElem.innerHTML;

                // Create a temporary textarea for copying
                const textarea = document.createElement("textarea");
                textarea.innerHTML = cleanHtml;
                textarea.style.position = "fixed";
                textarea.style.left = "-9999px";
                document.body.appendChild(textarea);

                // Select and copy the content
                textarea.select();
                document.execCommand("copy");
                document.body.removeChild(textarea);

                setCopySuccess(true);
                logEvent(analytics, "copied_rich_text_fallback", {});
            } catch (fallbackErr) {
                console.error("Failed to copy text (fallback): ", fallbackErr);
                logEvent(analytics, "failed_copy", {
                    error: fallbackErr.message,
                });
            }
        }
    };

    // Handle snackbar close
    const handleSnackbarClose = () => {
        setCopySuccess(false);
    };

    const generatePdf = async () => {
        setLoading((prev) => ({ ...prev, pdf: true }));
        logEvent(analytics, "clicked_pdf", {});
        const element = previewRef.current;

        // Configuration options for html2pdf
        const opt = {
            margin: [10, 10, 10, 10], // Margins: top, right, bottom, left
            filename: "document.pdf",
            image: { type: "jpeg", quality: 0.98 },
            html2canvas: {
                scale: 2, // Increases quality of the render
                useCORS: true, // Allows rendering of cross-origin images
            },
            jsPDF: {
                unit: "mm",
                format: "a4",
                orientation: "portrait",
            },
        };

        // Convert the element to PDF and trigger download
        html2pdf().set(opt).from(element).save();
        setLoading((prev) => ({ ...prev, pdf: false }));
    };

    const generateDocx = async () => {
        setLoading((prev) => ({ ...prev, docx: true }));
        logEvent(analytics, "clicked_word", {});

        try {
            if (!previewRef.current) {
                throw new Error("Preview content not found");
            }

            const doc = new Document({
                sections: [
                    {
                        properties: {},
                        children: convertHTMLToDocxContent(
                            previewRef.current.innerHTML
                        ),
                    },
                ],
            });

            const blob = await Packer.toBlob(doc);
            saveAs(blob, "document.docx");
        } catch (error) {
            console.error("DOCX generation error:", error);
            alert("Failed to generate Word document. Please try again.");
            logEvent(analytics, "failed_word", { error: error.message });
        } finally {
            setLoading((prev) => ({ ...prev, docx: false }));
        }
    };

    const convertHTMLToDocxContent = (html) => {
        // Create a temporary div to parse the HTML
        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = html;

        // Convert HTML elements to docx content
        const docxChildren = [];

        // Recursive processing of nodes
        const processNode = (node) => {
            switch (node.nodeType) {
                case Node.ELEMENT_NODE:
                    switch (node.tagName.toLowerCase()) {
                        // Headings
                        case "h1":
                            docxChildren.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_1,
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "h2":
                            docxChildren.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_2,
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "h3":
                            docxChildren.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_3,
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "h4":
                            docxChildren.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_4,
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "h5":
                            docxChildren.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_5,
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "h6":
                            docxChildren.push(
                                new Paragraph({
                                    heading: HeadingLevel.HEADING_6,
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;

                        // Text Formatting
                        case "strong":
                        case "b":
                            docxChildren.push(
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            bold: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "em":
                        case "i":
                            docxChildren.push(
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            italics: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "del":
                        case "s":
                            docxChildren.push(
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            strike: true,
                                        }),
                                    ],
                                })
                            );
                            break;
                        case "code":
                            docxChildren.push(
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            font: "Courier New",
                                        }),
                                    ],
                                })
                            );
                            break;

                        // Lists
                        case "ul":
                        case "ol":
                            Array.from(node.children).forEach((listItem) => {
                                docxChildren.push(
                                    new Paragraph({
                                        bullet: {
                                            level: 0,
                                        },
                                        children: [
                                            new TextRun(listItem.textContent),
                                        ],
                                    })
                                );
                            });
                            break;

                        // Links
                        case "a":
                            docxChildren.push(
                                new Paragraph({
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            color: "0000FF",
                                            underline: {
                                                type: "single",
                                            },
                                        }),
                                    ],
                                })
                            );
                            break;

                        // Blockquotes
                        case "blockquote":
                            docxChildren.push(
                                new Paragraph({
                                    border: {
                                        left: {
                                            color: "808080",
                                            space: 4,
                                            style: BorderStyle.SINGLE,
                                            size: 4,
                                        },
                                    },
                                    children: [
                                        new TextRun({
                                            text: node.textContent,
                                            italics: true,
                                        }),
                                    ],
                                })
                            );
                            break;

                        // Tables
                        case "table":
                            const tableRows = Array.from(
                                node.querySelectorAll("tr")
                            ).map(
                                (tr) =>
                                    new TableRow({
                                        children: Array.from(
                                            tr.querySelectorAll("td, th")
                                        ).map(
                                            (cell) =>
                                                new TableCell({
                                                    children: [
                                                        new Paragraph({
                                                            children: [
                                                                new TextRun(
                                                                    cell.textContent
                                                                ),
                                                            ],
                                                        }),
                                                    ],
                                                })
                                        ),
                                    })
                            );

                            docxChildren.push(
                                new Table({
                                    rows: tableRows,
                                    width: {
                                        size: 100,
                                        type: WidthType.PERCENTAGE,
                                    },
                                })
                            );
                            break;

                        // Paragraphs and other elements
                        case "p":
                            docxChildren.push(
                                new Paragraph({
                                    children: [new TextRun(node.textContent)],
                                })
                            );
                            break;

                        default:
                            // Recursively process child nodes for unknown elements
                            Array.from(node.childNodes).forEach(processNode);
                    }
                    break;

                case Node.TEXT_NODE:
                    if (node.textContent.trim()) {
                        docxChildren.push(
                            new Paragraph({
                                children: [new TextRun(node.textContent)],
                            })
                        );
                    }
                    break;
            }
        };

        // Start processing from the root
        processNode(tempDiv);

        return docxChildren;
    };

    const parseContentForGoogleDocs = (element) => {
        const requests = [];
        let currentIndex = 1; // Start at index 1 (beginning of the document)

        const traverseNodes = (node) => {
            // Handle different node types
            switch (node.nodeType) {
                case Node.ELEMENT_NODE:
                    switch (node.nodeName.toLowerCase()) {
                        case "h1":
                        case "h2":
                        case "h3":
                        case "h4":
                        case "h5":
                        case "h6":
                            // Insert heading text and a newline to create a new paragraph
                            requests.push({
                                insertText: {
                                    text: node.textContent + "\n",
                                    location: {
                                        index: currentIndex,
                                    },
                                },
                            });

                            // Apply the appropriate heading style
                            requests.push({
                                updateParagraphStyle: {
                                    paragraphStyle: {
                                        namedStyleType: `HEADING_${node.nodeName.charAt(
                                            1
                                        )}`, // e.g., HEADING_1, HEADING_2, etc.
                                    },
                                    fields: "namedStyleType",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length +
                                            1, // Include the newline
                                    },
                                },
                            });

                            currentIndex += node.textContent.length + 1; // Update index
                            break;

                        case "strong":
                        case "b":
                            requests.push({
                                updateTextStyle: {
                                    textStyle: { bold: true },
                                    fields: "bold",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "em":
                        case "i":
                            requests.push({
                                updateTextStyle: {
                                    textStyle: { italic: true },
                                    fields: "italic",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "u":
                            requests.push({
                                updateTextStyle: {
                                    textStyle: { underline: true },
                                    fields: "underline",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "del":
                        case "s":
                            requests.push({
                                updateTextStyle: {
                                    textStyle: { strikethrough: true },
                                    fields: "strikethrough",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "code":
                            requests.push({
                                updateTextStyle: {
                                    textStyle: {
                                        weightedFontFamily: {
                                            fontFamily: "Courier New",
                                        },
                                    },
                                    fields: "weightedFontFamily",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "a":
                            requests.push({
                                updateTextStyle: {
                                    textStyle: {
                                        link: {
                                            url: node.href,
                                        },
                                    },
                                    fields: "link",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "ul":
                        case "ol":
                            Array.from(node.children).forEach((li, index) => {
                                const text = `${
                                    node.nodeName === "ul"
                                        ? "• "
                                        : `${index + 1}. `
                                }${li.textContent}\n`;
                                requests.push({
                                    insertText: {
                                        text: text,
                                        location: {
                                            index: currentIndex,
                                        },
                                    },
                                });
                                currentIndex += text.length; // Update index
                            });
                            break;

                        case "blockquote":
                            requests.push({
                                updateParagraphStyle: {
                                    paragraphStyle: {
                                        borderLeft: {
                                            color: {
                                                color: {
                                                    rgbColor: {
                                                        red: 0.5,
                                                        green: 0.5,
                                                        blue: 0.5,
                                                    },
                                                },
                                            },
                                            width: {
                                                magnitude: 5,
                                                unit: "PT",
                                            },
                                            dashStyle: "SOLID",
                                        },
                                        indentStart: {
                                            magnitude: 20,
                                            unit: "PT",
                                        },
                                    },
                                    fields: "borderLeft,indentStart",
                                    range: {
                                        startIndex: currentIndex,
                                        endIndex:
                                            currentIndex +
                                            node.textContent.length,
                                    },
                                },
                            });
                            break;

                        case "table":
                            // Basic table support (simplified)
                            const tableRows = Array.from(
                                node.querySelectorAll("tr")
                            ).map((tr) =>
                                Array.from(tr.querySelectorAll("td, th")).map(
                                    (cell) => cell.textContent
                                )
                            );
                            const tableText =
                                tableRows
                                    .map((row) => row.join("\t"))
                                    .join("\n") + "\n";
                            requests.push({
                                insertText: {
                                    text: tableText,
                                    location: {
                                        index: currentIndex,
                                    },
                                },
                            });
                            currentIndex += tableText.length; // Update index
                            break;
                    }
                    break;

                case Node.TEXT_NODE:
                    if (node.textContent.trim()) {
                        requests.push({
                            insertText: {
                                text: node.textContent + "\n",
                                location: {
                                    index: currentIndex,
                                },
                            },
                        });
                        currentIndex += node.textContent.length + 1; // Update index
                    }
                    break;
            }

            // Recursively process child nodes (if not already processed)
            if (node.childNodes && !node.nodeName.match(/^h[1-6]$/i)) {
                Array.from(node.childNodes).forEach(traverseNodes);
            }
        };

        // Start traversing from the root element
        traverseNodes(element);
        return requests;
    };

    const handleGoogleDocs = async (accessToken) => {
        const token = googleToken || accessToken || null;
        logEvent(analytics, "clicked_google", {});
        if (!token) return setAuthOpen(true);

        setLoading((prev) => ({ ...prev, google: true }));
        try {
            if (!previewRef.current) {
                throw new Error("Preview content not found");
            }

            // Create the document
            const createDocResponse = await fetch(
                "https://docs.googleapis.com/v1/documents",
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        title: `Markdown Converter Document - ${new Date().toLocaleString()}`,
                    }),
                }
            );

            if (!createDocResponse.ok)
                throw new Error("Failed to create document");

            const { documentId } = await createDocResponse.json();

            // Parse and add content
            const contentRequests = parseContentForGoogleDocs(
                previewRef.current
            );

            // Batch update the document
            const batchUpdateResponse = await fetch(
                `https://docs.googleapis.com/v1/documents/${documentId}:batchUpdate`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        requests: contentRequests,
                    }),
                }
            );

            if (!batchUpdateResponse.ok)
                throw new Error("Failed to update document");

            // Open the created document
            window.open(
                `https://docs.google.com/document/d/${documentId}/edit`,
                "_blank"
            );
        } catch (error) {
            console.error("Google Docs error:", error);
            alert(
                "Failed to create Google Doc. Please check your authentication and content."
            );
            logEvent(analytics, "failed_google", { error: error.message });
        } finally {
            setLoading((prev) => ({ ...prev, google: false }));
        }
    };

    return (
        <ThemeProvider theme={theme}>
            <Root>
                <SEO />
                <CssBaseline />
                <AppBar
                    position="static"
                    color="default"
                    sx={{
                        bgcolor:
                            theme.palette.mode === "dark"
                                ? "#121826"
                                : "#f8fafc",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}
                >
                    <StyledToolbar>
                        <LeftSection>
                            <Typography
                                variant={isMobile ? "h5" : "h4"}
                                component="div"
                                sx={{
                                    fontWeight: "bold",
                                    fontFamily: "Monospace",
                                    color:
                                        theme.palette.mode === "dark"
                                            ? "#e2e8f0"
                                            : "#1e293b",
                                }}
                            >
                                # Markdown Converter Pro
                            </Typography>
                        </LeftSection>
                        <RightSection>
                            <IconButton
                                onClick={toggleDarkMode}
                                sx={{
                                    color:
                                        theme.palette.mode === "dark"
                                            ? "#e2e8f0"
                                            : "#1e293b",
                                }}
                            >
                                {theme.palette.mode === "dark" ? (
                                    <LightMode />
                                ) : (
                                    <DarkMode />
                                )}
                            </IconButton>
                            <StyledButton
                                variant="contained"
                                startIcon={<ContentCopy />}
                                onClick={handleCopy}
                                sx={{
                                    bgcolor: "#3b82f6",
                                    "&:hover": { bgcolor: "#2563eb" },
                                }}
                            >
                                Copy
                            </StyledButton>
                            <StyledButton
                                variant="contained"
                                startIcon={<CloudDownload />}
                                onClick={generatePdf}
                                disabled={loading.pdf}
                                sx={{
                                    bgcolor: "#10b981",
                                    "&:hover": { bgcolor: "#059669" },
                                }}
                            >
                                {loading.pdf ? "Generating..." : "PDF"}
                            </StyledButton>
                            <StyledButton
                                variant="contained"
                                startIcon={<CloudDownload />}
                                onClick={generateDocx}
                                disabled={loading.docx}
                                sx={{
                                    bgcolor: "#6366f1",
                                    "&:hover": { bgcolor: "#4f46e5" },
                                }}
                            >
                                {loading.docx ? "Generating..." : "Word"}
                            </StyledButton>
                            <StyledButton
                                variant="contained"
                                startIcon={<Google />}
                                onClick={() => handleGoogleDocs()}
                                disabled={loading.google}
                                sx={{
                                    bgcolor: "#f43f5e",
                                    "&:hover": { bgcolor: "#e11d48" },
                                }}
                            >
                                {loading.google
                                    ? "Processing..."
                                    : "Google Docs"}
                            </StyledButton>
                        </RightSection>
                    </StyledToolbar>
                </AppBar>
                <ContentContainer container>
                    <EditorContainer item xs={12} md={6}>
                        <Box
                            sx={{
                                height: "100%",
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "#1a202c"
                                        : "#f8fafc",
                                "& ::-webkit-scrollbar": { width: "8px" },
                                "& ::-webkit-scrollbar-track": {
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "#0f172a"
                                            : "#e2e8f0",
                                },
                                "& ::-webkit-scrollbar-thumb": {
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "#4a5568"
                                            : "#94a3b8",
                                    borderRadius: "4px",
                                },
                            }}
                        >
                            <Editor value={markdown} onChange={setMarkdown} />
                        </Box>
                    </EditorContainer>
                    <PreviewContainer item xs={12} md={6}>
                        <Box
                            sx={{
                                height: "100%",
                                bgcolor:
                                    theme.palette.mode === "dark"
                                        ? "#1a202c"
                                        : "#f8fafc",
                                "& ::-webkit-scrollbar": { width: "8px" },
                                "& ::-webkit-scrollbar-track": {
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "#0f172a"
                                            : "#e2e8f0",
                                },
                                "& ::-webkit-scrollbar-thumb": {
                                    bgcolor:
                                        theme.palette.mode === "dark"
                                            ? "#4a5568"
                                            : "#94a3b8",
                                    borderRadius: "4px",
                                },
                            }}
                        >
                            <Preview ref={previewRef} value={markdown} />
                        </Box>
                    </PreviewContainer>
                </ContentContainer>
                <Auth
                    open={authOpen}
                    onClose={(accessToken) => {
                        setAuthOpen(false);
                        if (accessToken) {
                            setGoogleToken(accessToken);
                            handleGoogleDocs(accessToken);
                        }
                    }}
                />
                <Snackbar
                    open={copySuccess}
                    autoHideDuration={2000}
                    onClose={handleSnackbarClose}
                    message="Copied with formatting!"
                    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                />
            </Root>
        </ThemeProvider>
    );
}
